import { NotificationVariants, type Quote, type ToastProps } from '@talos/kyoko';

import { cancelQuote, type CancelQuoteDeps } from './cancelQuote';

export type CancelQuoteInput = Quote | string;
export type CancelMultipleQuotesResult = PromiseSettledResult<Quote> & { quoteReqID: string };

export interface CancelMultipleQuotesSummary {
  totalCount: number;
  successfulCount: number;
  failedCount: number;
  results: CancelMultipleQuotesResult[];
}

export interface CancelMultipleQuotesDeps extends CancelQuoteDeps {
  addToast: (toast: ToastProps) => void;
}

/**
 * Cancels multiple quotes concurrently and returns the full settled list for callers
 * that need local UI state updates. Toast handling intentionally stays aggregate-only.
 */
export async function cancelMultipleQuotes(
  quotesOrParams: CancelQuoteInput[],
  { addToast, ...cancelQuoteDeps }: CancelMultipleQuotesDeps
): Promise<CancelMultipleQuotesSummary> {
  if (quotesOrParams.length === 0) {
    return {
      totalCount: 0,
      successfulCount: 0,
      failedCount: 0,
      results: [],
    };
  }

  const quoteReqIDs = quotesOrParams.map(getQuoteReqID);
  // Run all cancels independently so one publication failure does not block the rest.
  const settledResults = await Promise.allSettled(
    quoteReqIDs.map(quoteReqID => cancelQuote(quoteReqID, cancelQuoteDeps))
  );
  const results = settledResults.map((result, index) => ({
    quoteReqID: quoteReqIDs[index],
    ...result,
  }));

  const successfulCount = results.filter(result => result.status === 'fulfilled').length;
  const failedCount = results.length - successfulCount;

  addToast({
    text: `${successfulCount}/${quotesOrParams.length} quotes canceled successfully`,
    variant: getAggregateVariant(successfulCount, quotesOrParams.length),
    dismissable: true,
    timeout: 5000,
  });

  return {
    totalCount: quotesOrParams.length,
    successfulCount,
    failedCount,
    results,
  };
}

function getQuoteReqID(input: CancelQuoteInput): string {
  return typeof input === 'string' ? input : input.QuoteReqID;
}

function getAggregateVariant(successfulCount: number, totalCount: number): NotificationVariants {
  if (successfulCount === totalCount) {
    return NotificationVariants.Positive;
  }

  if (successfulCount > 0) {
    return NotificationVariants.Warning;
  }

  return NotificationVariants.Negative;
}
